/**
 * Adding a member.
 *
 * One form, three tables. A manager thinking "the Ashfords are joining as
 * a full golf family from the first of the month" should not have to know
 * that BetterClub stores that as a person, an account and a membership,
 * and should not be able to half-create it.
 *
 * The additional-people rows are the part that earns its keep. Almost
 * every club membership is more than one person — a spouse who signs
 * chits, children who may use the pool but not the bar, corporate
 * signatories — and software that models a membership as one name forces
 * clubs into workarounds that surface years later as a disputed charge
 * nobody can attribute.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { BylawNotices, Card, ErrorBanner } from '../components/ui.tsx';

interface Category {
  id: string;
  code: string;
  name: string;
  active_memberships: number;
}

interface PersonDraft {
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  role: 'spouse' | 'dependent' | 'authorized_user';
  may_charge: boolean;
}

const blankPerson = (role: PersonDraft['role'] = 'spouse'): PersonDraft => ({
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  role,
  may_charge: role !== 'dependent',
});

export function NewMemberPage() {
  const navigate = useNavigate();
  const categories = useApi<{ categories: Category[] }>('/v1/membership-categories');

  const [salutation, setSalutation] = useState('Mr.');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');

  const [others, setOthers] = useState<PersonDraft[]>([]);

  const [category, setCategory] = useState('');
  const [joinedOn, setJoinedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [accountNumber, setAccountNumber] = useState('');
  const [displayName, setDisplayName] = useState('');

  const [line1, setLine1] = useState('');
  const [locality, setLocality] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const create = useMutation(async (body: Record<string, unknown>) =>
    api<{ member_account_id: string; account_number: string; display_name: string }>('/v1/members', {
      method: 'POST',
      body,
    }),
  );

  // Shown as a placeholder, not silently applied. A club that addresses
  // statements differently should see what BetterClub was going to write
  // before it writes it. Mirrors the server's rule exactly — including
  // that "Mr. and Mrs. Thomas Ashford" is only used for a Mr./Mrs. pair,
  // because "Dr. and Mr. Eleanor Prescott" is not how anyone is addressed.
  const spouse = others.find(
    (o) => o.role === 'spouse' && (o.last_name || lastName) === lastName && o.first_name.trim(),
  );
  const title = (s: string) => s.trim().replace(/\.$/, '').toLowerCase();
  const own = [salutation, firstName, lastName].filter(Boolean).join(' ');
  const suggestedName = (() => {
    if (!spouse || !salutation || !spouse.salutation) return own;
    const pair = [title(salutation), title(spouse.salutation)];
    if (pair.every((t) => t === 'mr' || t === 'mrs') && pair[0] !== pair[1]) {
      const mister = title(salutation) === 'mr' ? firstName : spouse.first_name;
      return `Mr. and Mrs. ${mister} ${lastName}`;
    }
    return `${salutation} ${firstName} and ${spouse.salutation} ${spouse.first_name} ${lastName}`;
  })();

  const ready = firstName.trim() && lastName.trim() && category && joinedOn;

  return (
    <>
      <h1>Add a Member</h1>
      {/* Article VII and whatever else the club says about proposing
          somebody. Shown while the candidate is being put forward, which
          is the only moment it can still change the outcome. */}
      <BylawNotices surface="member.new" />

      <ErrorBanner error={create.error} />

      <Card title="The Member">
        <div className="row">
          <label>
            <span className="muted small">Title</span>
            <input
              value={salutation}
              onChange={(e) => setSalutation(e.target.value)}
              style={{ maxWidth: 90 }}
            />
          </label>
          <label>
            <span className="muted small">First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
          </label>
          <label>
            <span className="muted small">Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <label style={{ flex: 1 }}>
            <span className="muted small">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label style={{ flex: 1 }}>
            <span className="muted small">Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label>
            <span className="muted small">Birthday</span>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </label>
        </div>
      </Card>

      <Card
        title="Others on the Membership"
        actions={
          <>
            <button className="secondary small" onClick={() => setOthers([...others, blankPerson('spouse')])}>
              + Spouse
            </button>{' '}
            <button
              className="secondary small"
              onClick={() => setOthers([...others, blankPerson('dependent')])}
            >
              + Dependent
            </button>{' '}
            <button
              className="secondary small"
              onClick={() => setOthers([...others, blankPerson('authorized_user')])}
            >
              + Authorized user
            </button>
          </>
        }
      >
        {others.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            Anyone added here can be given the right to sign a chit. Birthdays matter for more
            than cards — a junior ages out of their category on one.
          </p>
        ) : (
          others.map((p, i) => (
            <div key={i} className="row" style={{ marginBottom: 10, alignItems: 'flex-end' }}>
              <label>
                <span className="muted small">Title</span>
                <input
                  value={p.salutation}
                  onChange={(e) => patch(setOthers, others, i, { salutation: e.target.value })}
                  style={{ maxWidth: 90 }}
                />
              </label>
              <label>
                <span className="muted small">First name</span>
                <input
                  value={p.first_name}
                  onChange={(e) => patch(setOthers, others, i, { first_name: e.target.value })}
                />
              </label>
              <label>
                <span className="muted small">Last name</span>
                <input
                  value={p.last_name}
                  onChange={(e) => patch(setOthers, others, i, { last_name: e.target.value })}
                  placeholder={lastName}
                />
              </label>
              <label>
                <span className="muted small">Relationship</span>
                <select
                  value={p.role}
                  onChange={(e) =>
                    patch(setOthers, others, i, { role: e.target.value as PersonDraft['role'] })
                  }
                >
                  <option value="spouse">Spouse</option>
                  <option value="dependent">Dependent</option>
                  <option value="authorized_user">Authorized user</option>
                </select>
              </label>
              <label>
                <span className="muted small">Email</span>
                <input
                  type="email"
                  value={p.email}
                  onChange={(e) => patch(setOthers, others, i, { email: e.target.value })}
                />
              </label>
              <label>
                <span className="muted small">Phone</span>
                <input
                  value={p.phone}
                  onChange={(e) => patch(setOthers, others, i, { phone: e.target.value })}
                  style={{ maxWidth: 150 }}
                />
              </label>
              <label>
                <span className="muted small">Birthday</span>
                <input
                  type="date"
                  value={p.date_of_birth}
                  onChange={(e) => patch(setOthers, others, i, { date_of_birth: e.target.value })}
                />
              </label>
              <label className="muted small" style={{ display: 'flex', gap: 6, paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={p.may_charge}
                  onChange={(e) => patch(setOthers, others, i, { may_charge: e.target.checked })}
                />
                May charge
              </label>
              <button
                className="secondary small"
                onClick={() => setOthers(others.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </Card>

      <Card title="The Membership">
        <div className="row">
          <label>
            <span className="muted small">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Choose…</option>
              {(categories.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Member since</span>
            <input type="date" value={joinedOn} onChange={(e) => setJoinedOn(e.target.value)} />
          </label>
          <label>
            <span className="muted small">Account number</span>
            <input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="next available"
              className="mono"
              style={{ maxWidth: 160 }}
            />
          </label>
        </div>
        <p className="muted small" style={{ marginTop: 8 }}>
          The joining date is what dues are prorated from, and it is what the club means by "member
          since" for as long as they belong — a category change later does not reset it.
        </p>
      </Card>

      <Card title="Statement">
        <div className="row">
          <label style={{ flex: 1 }}>
            <span className="muted small">Addressed to</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={suggestedName}
            />
          </label>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <label style={{ flex: 2 }}>
            <span className="muted small">Address</span>
            <input value={line1} onChange={(e) => setLine1(e.target.value)} />
          </label>
          <label>
            <span className="muted small">Town</span>
            <input value={locality} onChange={(e) => setLocality(e.target.value)} />
          </label>
          <label>
            <span className="muted small">State</span>
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              style={{ maxWidth: 140 }}
            />
          </label>
          <label>
            <span className="muted small">ZIP</span>
            <input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              style={{ maxWidth: 110 }}
              className="mono"
            />
          </label>
        </div>
      </Card>

      <div className="row" style={{ marginBottom: 40 }}>
        <button
          className="gold"
          disabled={!ready || create.busy}
          onClick={async () => {
            const result = await create.run({
              primary: {
                salutation: salutation || null,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim() || null,
                phone: phone.trim() || null,
                date_of_birth: dob || null,
              },
              additional_people: others
                .filter((p) => p.first_name.trim())
                .map((p) => ({
                  salutation: p.salutation || null,
                  first_name: p.first_name.trim(),
                  // Sharing a last name is the common case and re-typing it
                  // is where the typos come from.
                  last_name: (p.last_name || lastName).trim(),
                  email: p.email.trim() || null,
                  phone: p.phone.trim() || null,
                  date_of_birth: p.date_of_birth || null,
                  role: p.role,
                  may_charge: p.may_charge,
                })),
              membership_category: category,
              joined_on: joinedOn,
              ...(accountNumber.trim() ? { account_number: accountNumber.trim() } : {}),
              ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
              ...(line1.trim() && locality.trim()
                ? {
                    billing_address: {
                      lines: [line1.trim()],
                      locality: locality.trim(),
                      region: region.trim() || null,
                      postal_code: postalCode.trim() || null,
                      country_code: 'US',
                    },
                  }
                : {}),
            });

            if (result) navigate(`/members/${result.member_account_id}`);
          }}
        >
          {create.busy ? 'Adding…' : 'Add member'}
        </button>
        <button className="secondary" onClick={() => navigate('/members')}>
          Cancel
        </button>
      </div>
    </>
  );
}

function patch<T>(
  set: (next: T[]) => void,
  list: T[],
  index: number,
  changes: Partial<T>,
): void {
  set(list.map((item, i) => (i === index ? { ...item, ...changes } : item)));
}
